import { useEffect, useMemo, useState } from 'react';
import { graphql } from 'gatsby';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout/Layout';
import UtilsService from '../Services/UtilsService';
import InviteHero from '../components/Hero/InvitesHero/InvitesHero';
import FeatureIntegrationBlock from '../components/FeatureBlocks/FeatureIntegrationBlock/FeatureIntegrationBlock';
import FeatureValuePropsBlock from '../components/FeatureBlocks/FeatureValuePropsBlock/FeatureValuePropsBlock';
import TestimonialCardsBlock from '../components/FeatureBlocks/TestimonialCardsBlock/TestimonialCardsBlock';
import SmarterSystemBlock from '../components/FeatureBlocks/SmarterSystemBlock/SmarterSystemBlock';
import PromoBlockLeft from '../components/FeatureBlocks/PromoBlockLeft/PromoBlockLeft';
import useReferral from '../hooks/useReferral';
import getApiUrl from '../env';

import '../styles/invites-page.scss';
import AnalyticsService from '../Services/AnalyticsService';

const InvitePage = () => {
    const { t } = useTranslation();

    const code = UtilsService.getParameterByName('code');
    const [heroTitle, setHeroTitle] = useState('');
    const [promoBlockLeftTitle, setPromoBlockLeftTitle] = useState('');
    const [isFallback, setIsFallback] = useState(false);
    const [promoBlockLeftSubtitle, setPromoBlockLeftSubtitle] = useState('');
    const { storeReferralData } = useReferral();

    const fallbackHeroTitle = t('invitesPage.hero.withoutCode.title');

    const fallbackPromoBlockTitle = t('invitesPage.promoBlock.fallbackTitle');

    const ogTags = useMemo(
        () => ({
            url: `https://www.honeybook.com/invites?code=${code}`,
            title: 'Client Management Software for Small Businesses',
            description:
                "Get clients faster with our all-in-one project, invoicing and payments management tool. HoneyBook's robust features help small businesses manage everything from brochures and proposals to contracts and online payments. Free 30-day Trial - No Credit Card Required. Voted #1 Small Business Client Management Platform."
        }),
        [code]
    );

    const setContent = (name, discount, duration) => {
        setHeroTitle(
            t('invitesPage.hero.withCode', {
                name,
                discount,
                duration: duration.replace('one', 'first')
            })
        );
        setPromoBlockLeftTitle(
            <>
                {t('invitesPage.promoBlock.title1')}
                {name}
                <br /> {t('invitesPage.promoBlock.title2')}
            </>
        );
        setPromoBlockLeftSubtitle(
            <>
                {t('invitesPage.promoBlock.subtitle1')} {discount}
                {t('invitesPage.promoBlock.subtitle2')} {duration}
            </>
        );
    };

    const setFallback = () => {
        // test locally (without api call)
        // setContent('Bruce Wayne', '60% off', 'one year');
        setIsFallback(true);
        setHeroTitle(fallbackHeroTitle);
        setPromoBlockLeftTitle(fallbackPromoBlockTitle);
    };

    useEffect(() => {
        AnalyticsService.track('loaded: referral recipient experience', {
            recipient_type: 'marketing_site',
            source: UtilsService.getParameterByName('utm_source')
        });
        const url = `${getApiUrl()}/api/referrer_marketing?code=${code}`;

        if (code) {
            if (code === 'weimuzheng4736925') {
                window.location.replace('https://www.honeybook.com');
            } else {
                window.location.replace(
                    `${window.location.origin}/signup?redirect=true&code=${code}`
                );
            }

            fetch(url)
                .then(response => response.json())
                .then(data => {
                    if (data.user.full_name != null) {
                        setContent(
                            data.user.full_name,
                            data.discount_amount,
                            data.discount_duration
                        );
                        storeReferralData(data);
                    } else {
                        // Code was invalid
                        setFallback();
                    }
                })
                // Fetch error
                .catch(() => setFallback());
        } else {
            // No code was applied
            setFallback();
        }
    }, []);

    return (
        <Layout ogTags={ogTags}>
            <div className="invites-lp">
                <InviteHero title={heroTitle} isFallback={isFallback} />
                <FeatureValuePropsBlock
                    hideCta={true}
                    title={t('featureValueProps.title')}
                />
                <FeatureIntegrationBlock
                    title={t('featureIntegrationBlock.title')}
                    subtitle={t('featureIntegrationBlock.subtitle')}
                />
                <SmarterSystemBlock
                    title={t('invitesPage.smarterSystems.title')}
                    subtitle={t('invitesPage.smarterSystems.subtitle')}
                    ctaText={
                        isFallback
                            ? undefined
                            : t('invitesPage.smarterSystems.ctaText')
                    }
                />
                <TestimonialCardsBlock hideReviewsLink />
                <PromoBlockLeft
                    title={promoBlockLeftTitle}
                    subtitle={promoBlockLeftSubtitle}
                    ctaText={
                        isFallback
                            ? undefined
                            : t('invitesPage.promoBlock.ctaText')
                    }
                    customNoCcText={t('invitesPage.promoBlock.customNoCcText')}
                    customClass="promo-block-left--yellow promo-block-left--invites"
                    source="promo block left - yellow"
                    buttonStyle="primary--dark"
                />
            </div>
        </Layout>
    );
};

export default InvitePage;

export const query = graphql`
    query ($language: String!) {
        locales: allLocale(
            filter: {
                ns: { in: ["common", "invites"] }
                language: { eq: $language }
            }
        ) {
            edges {
                node {
                    ns
                    data
                    language
                }
            }
        }
    }
`;
